import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApplicationFormPageEnum } from '../types';
import { Button } from '@components/common/Button';
import { FormControl } from '@components/common/FormControl/FormControl';
import { FormCurrencyField } from '@components/common/FormCurrencyField/FormCurrencyField';
import { InputAdornment } from '@components/common/InputAdornment';
import { householdOutgoingsQuestions } from './questions';
import { IHouseholdOutgoingsForm } from './types';
import { getCurrentRoute } from '@utils/location';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';

export const HouseholdOutgoings = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(id);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IHouseholdOutgoingsForm>({
    defaultValues: {
      childcare: '',
      hasChildcare: null,
      educationFees: '',
      hasEducationFees: null,
      maintenance: '',
      hasMaintenance: null,
    },
  });

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const { childcare, educationFees, maintenance } = parseApplicationInProgress(applicationInProgress[currentRoute]);

      form.reset({
        childcare,
        hasChildcare: childcare ? 'true' : 'false',
        educationFees,
        hasEducationFees: educationFees ? 'true' : 'false',
        maintenance,
        hasMaintenance: maintenance ? 'true' : 'false',
      });
    }
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IHouseholdOutgoingsForm) => {
    const { childcare, educationFees, maintenance } = formData;

    const questions = Object.entries({ childcare, educationFees, maintenance }).map(([key, answer]) => ({
      question: intl.formatMessage({ id: householdOutgoingsQuestions[key].labelKey }),
      key,
      answer: answer || 0,
    }));

    patchApplicationInProgress({ questions, currentRoute, applicationId: id });

    searchParams.set('section', ApplicationFormPageEnum.TRAVEL_EXPENSES);
    setSearchParams(searchParams);
  };

  const formState = form.watch();

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <FormattedMessage id={'applicationForm.HOUSEHOLD_OUTGOINGS.header'} tagName={'h4'} />
        {Object.entries(householdOutgoingsQuestions).map(([key, question]) => (
          <section key={key} className={key}>
            <FormattedMessage id={question.labelKey} tagName={'h4'} />
            {question.explanationKey && <FormattedMessage id={question.explanationKey} tagName={'p'} />}

            {!formState[question.booleanQuestionFormKey as keyof IHouseholdOutgoingsForm] ||
            formState[question.booleanQuestionFormKey as keyof IHouseholdOutgoingsForm] === 'false' ? (
              <FormControl
                rules={{ required: requiredFieldErrorMessage }}
                name={question.booleanQuestionFormKey}
                radioOptions={question.options}
                radioGroupProps={{ className: 'mt-2' }}
                onChangeEffect={(event: React.ChangeEvent<HTMLInputElement>) => {
                  form.setValue(question.booleanQuestionFormKey, event.target.value);
                }}
              />
            ) : (
              <FormCurrencyField
                name={key}
                rules={{
                  required: requiredFieldErrorMessage,
                  validate: value => Number(value) >= 0,
                }}
                textFieldProps={{
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <FormattedMessage id={question.adornmentKey} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </section>
        ))}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
